import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { extractError } from '../context/AuthContext';
import {
  Check,
  Save,
  ChevronDown,
  ChevronUp,
  GripVertical,
  User,
  Briefcase,
  Globe,
  Award,
  GraduationCap,
  Plus,
  Trash2,
  X,
  ShieldCheck,
  FileText,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import './CandidateReview.css';

export default function CandidateReview() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({
    personal: true,
    experiences: true,
    languages: true,
    certifications: true,
    formations: true,
  });

  useEffect(() => {
    if (!token) {
      setError('Lien invalide ou expiré.');
      setLoading(false);
      return;
    }

    api.get(`/cv/review/${token}`)
      .then((res) => {
        setCvData(res.data.json_cv);
        setOwnerName(res.data.nom_proprietaire || '');
      })
      .catch(() => setError('Ce lien est invalide ou a expiré.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/cv/review/${token}`, { json_cv: cvData });
      setSaved(true);
      toast.success('Vos modifications ont été enregistrées et envoyées !');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  const setPersonal = (k, v) =>
    setCvData((d) => ({
      ...d,
      personal_info: {
        ...d.personal_info,
        [k]: v,
      },
    }));

  const addExp = () =>
    setCvData((d) => ({
      ...d,
      experiences: [
        ...(d.experiences || []),
        {
          entreprise: '',
          dates: '',
          poste: '',
          synopsis: '',
          realisations: [''],
          methodologies: [],
          technologies: [],
        },
      ],
    }));

  const updateExp = (i, k, v) =>
    setCvData((d) => {
      const exp = [...d.experiences];
      exp[i] = { ...exp[i], [k]: v };
      return { ...d, experiences: exp };
    });

  const removeExp = (i) =>
    setCvData((d) => ({
      ...d,
      experiences: d.experiences.filter((_, idx) => idx !== i),
    }));

  const addTask = (ei) =>
    setCvData((d) => {
      const exp = [...d.experiences];
      exp[ei] = {
        ...exp[ei],
        realisations: [...(exp[ei].realisations || []), ''],
      };
      return { ...d, experiences: exp };
    });

  const updateTask = (ei, ti, v) =>
    setCvData((d) => {
      const exp = [...d.experiences];
      const r = [...(exp[ei].realisations || [])];
      r[ti] = v;
      exp[ei] = { ...exp[ei], realisations: r };
      return { ...d, experiences: exp };
    });

  const removeTask = (ei, ti) =>
    setCvData((d) => {
      const exp = [...d.experiences];
      exp[ei].realisations = exp[ei].realisations.filter((_, i) => i !== ti);
      return { ...d, experiences: exp };
    });

  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  if (loading) {
    return (
      <div className="cr-state">
        <div className="cr-state-card">
          <div className="cr-state-icon cr-state-icon--neutral">
            <div className="cr-spinner" />
          </div>
          <h2 className="cr-state-title">Chargement de votre CV</h2>
          <p className="cr-state-text">
            Merci de patienter quelques instants pendant la récupération de vos informations.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cr-state">
        <div className="cr-state-card">
          <div className="cr-state-icon cr-state-icon--error">
            <AlertCircle size={30} />
          </div>
          <h2 className="cr-state-title">Lien invalide</h2>
          <p className="cr-state-text">{error}</p>
        </div>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="cr-state">
        <div className="cr-state-card">
          <div className="cr-state-icon cr-state-icon--success">
            <Check size={30} />
          </div>
          <h2 className="cr-state-title">Modifications enregistrées</h2>
          <p className="cr-state-text">
            Vos modifications ont bien été enregistrées et transmises à votre consultant.
            Merci pour votre relecture.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-page">
      <div className="cr-container">
        <section className="cr-hero">
          <div className="cr-hero-inner">
            <div className="cr-topbar">
              <div className="cr-brand">
                {/* Logo — trois barres setycorp */}
                <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink:0 }}>
                  <g transform="skewX(-18)">
                    <rect x="0"  y="0"  width="7" height="36" rx="2" fill="#FF4100"/>
                    <rect x="11" y="7"  width="7" height="29" rx="2" fill="#FF4100" fillOpacity="0.72"/>
                    <rect x="22" y="14" width="7" height="22" rx="2" fill="#FF4100" fillOpacity="0.44"/>
                  </g>
                </svg>
                <div className="cr-brandtext">
                  <span className="cr-brandname">WIBusiness</span>
                  <span className="cr-brandsub">by Wikeys</span>
                </div>
              </div>

              <div className="cr-badge">Espace sécurisé de relecture</div>
            </div>

            <div className="cr-hero-content">
              <div>
                <h1 className="cr-title">
                  Relisez et mettez à jour
                  <span> votre CV</span>
                </h1>
                <p className="cr-subtitle">
                  Votre consultant a préparé une version structurée de votre CV.
                  Vérifiez les informations ci-dessous, corrigez-les si nécessaire,
                  puis enregistrez vos modifications.
                </p>
              </div>

              <div className="cr-sidecard">
                <div className="cr-sidecard-label">Candidat</div>
                <div className="cr-sidecard-value">
                  {ownerName || 'Profil candidat'}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="cr-info">
          <div className="cr-info-icon">
            <ShieldCheck size={18} />
          </div>
          <div>
            <div className="cr-info-title">Révision confidentielle</div>
            <div className="cr-info-text">
              Les modifications enregistrées seront transmises directement à votre consultant
              pour mise à jour de votre dossier.
            </div>
          </div>
        </div>

        <div className="cr-layout">
          <main className="cr-main">
            <section className="cr-card">
              <button
                type="button"
                className="cr-card-header cr-card-header--button"
                onClick={() => toggle('personal')}
              >
                <div className="cr-card-title-wrap">
                  <User size={16} color="var(--cr-blue-600)" />
                  <span className="cr-card-title">Informations personnelles</span>
                </div>
                {expanded.personal ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {expanded.personal && (
                <div className="cr-card-body">
                  <div className="cr-grid cr-grid-2">
                    {[
                      { k: 'prenom', label: 'Prénom' },
                      { k: 'nom', label: 'Nom' },
                      { k: 'trigramme', label: 'Trigramme' },
                      { k: 'poste', label: 'Titre / Poste' },
                      { k: 'annees_experience', label: "Années d'expérience" },
                    ].map(({ k, label }) => (
                      <div key={k} className="cr-field">
                        <label className="cr-label">{label}</label>
                        <input
                          className="cr-input"
                          value={cvData?.personal_info?.[k] || ''}
                          onChange={(e) => setPersonal(k, e.target.value)}
                        />
                      </div>
                    ))}

                    <div className="cr-field cr-col-full">
                      <label className="cr-label">Présentation professionnelle</label>
                      <textarea
                        className="cr-textarea"
                        rows={4}
                        value={cvData?.personal_info?.presentation || ''}
                        onChange={(e) => setPersonal('presentation', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="cr-card">
              <div className="cr-card-header">
                <div className="cr-card-title-wrap">
                  <Briefcase size={16} color="var(--cr-blue-600)" />
                  <span className="cr-card-title">Expériences</span>
                  <span className="cr-pill">{cvData?.experiences?.length || 0}</span>
                </div>

                <button className="cr-btn cr-btn-secondary cr-btn-sm" onClick={addExp}>
                  <Plus size={13} />
                  Ajouter
                </button>
              </div>

              <div className="cr-card-body">
                <div className="cr-stack">
                  {(cvData?.experiences || []).map((exp, i) => (
                    <div key={i} className="cr-exp-block">
                      <div className="cr-exp-header">
                        <div className="cr-exp-handle">
                          <GripVertical size={14} />
                        </div>

                        <div className="cr-grid cr-grid-2 cr-exp-grid">
                          <div className="cr-field">
                            <label className="cr-label">Poste</label>
                            <input
                              className="cr-input"
                              value={exp.poste || ''}
                              onChange={(e) => updateExp(i, 'poste', e.target.value)}
                            />
                          </div>

                          <div className="cr-field">
                            <label className="cr-label">Entreprise</label>
                            <input
                              className="cr-input"
                              value={exp.entreprise || ''}
                              onChange={(e) => updateExp(i, 'entreprise', e.target.value)}
                            />
                          </div>

                          <div className="cr-field cr-col-full">
                            <label className="cr-label">Dates</label>
                            <input
                              className="cr-input"
                              value={exp.dates || ''}
                              onChange={(e) => updateExp(i, 'dates', e.target.value)}
                              placeholder="Ex: Janvier 2022 - Présent"
                            />
                          </div>

                          <div className="cr-field cr-col-full">
                            <label className="cr-label">Synopsis / Contexte</label>
                            <input
                              className="cr-input"
                              value={exp.synopsis || ''}
                              onChange={(e) => updateExp(i, 'synopsis', e.target.value)}
                            />
                          </div>
                        </div>

                        <button
                          className="cr-btn cr-btn-danger cr-btn-icon cr-btn-sm"
                          onClick={() => removeExp(i)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>

                      <div className="cr-task-box">
                        <div className="cr-task-box-header">
                          <div className="cr-task-box-title">Réalisations</div>
                          <button
                            className="cr-btn cr-btn-ghost cr-btn-sm"
                            onClick={() => addTask(i)}
                          >
                            <Plus size={12} />
                            Ajouter
                          </button>
                        </div>

                        {(exp.realisations || []).map((t, ti) => (
                          <div key={ti} className="cr-task-row">
                            <div className="cr-task-dot" />
                            <input
                              className="cr-input cr-task-input"
                              value={t}
                              onChange={(e) => updateTask(i, ti, e.target.value)}
                              placeholder="Description…"
                            />
                            <button
                              className="cr-btn cr-btn-ghost cr-btn-icon cr-btn-sm"
                              onClick={() => removeTask(i, ti)}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="cr-field">
                        <label className="cr-label">Technologies</label>
                        <input
                          className="cr-input"
                          value={(exp.technologies || []).join(', ')}
                          onChange={(e) =>
                            updateExp(
                              i,
                              'technologies',
                              e.target.value
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            )
                          }
                          placeholder="Java, Spring Boot, React, SQL..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="cr-card">
              <button
                type="button"
                className="cr-card-header cr-card-header--button"
                onClick={() => toggle('languages')}
              >
                <div className="cr-card-title-wrap">
                  <Globe size={16} color="var(--cr-blue-600)" />
                  <span className="cr-card-title">Langues</span>
                </div>
                {expanded.languages ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {expanded.languages && (
                <div className="cr-card-body">
                  <div className="cr-card-toolbar">
                    <button
                      className="cr-btn cr-btn-secondary cr-btn-sm"
                      onClick={() =>
                        setCvData((d) => ({
                          ...d,
                          langues: [...(d.langues || []), { langue: '', niveau: '' }],
                        }))
                      }
                    >
                      <Plus size={13} />
                      Ajouter
                    </button>
                  </div>

                  <div className="cr-stack-sm">
                    {(cvData?.langues || []).map((l, i) => (
                      <div key={i} className="cr-inline-row cr-inline-row--3">
                        <div className="cr-field">
                          <label className="cr-label">Langue</label>
                          <input
                            className="cr-input"
                            value={l.langue || ''}
                            onChange={(e) => {
                              const lng = [...cvData.langues];
                              lng[i] = { ...lng[i], langue: e.target.value };
                              setCvData((d) => ({ ...d, langues: lng }));
                            }}
                          />
                        </div>

                        <div className="cr-field">
                          <label className="cr-label">Niveau</label>
                          <select
                            className="cr-input cr-select"
                            value={l.niveau || ''}
                            onChange={(e) => {
                              const lng = [...cvData.langues];
                              lng[i] = { ...lng[i], niveau: e.target.value };
                              setCvData((d) => ({ ...d, langues: lng }));
                            }}
                          >
                            {['', 'Débutant', 'Intermédiaire', 'Avancé', 'Courant', 'Bilingue', 'Natif'].map((n) => (
                              <option key={n}>{n}</option>
                            ))}
                          </select>
                        </div>

                        <button
                          className="cr-btn cr-btn-danger cr-btn-icon cr-btn-sm cr-inline-action"
                          onClick={() =>
                            setCvData((d) => ({
                              ...d,
                              langues: d.langues.filter((_, idx) => idx !== i),
                            }))
                          }
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="cr-card">
              <button
                type="button"
                className="cr-card-header cr-card-header--button"
                onClick={() => toggle('certifications')}
              >
                <div className="cr-card-title-wrap">
                  <Award size={16} color="var(--cr-blue-600)" />
                  <span className="cr-card-title">Certifications</span>
                </div>
                {expanded.certifications ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {expanded.certifications && (
                <div className="cr-card-body">
                  <div className="cr-card-toolbar">
                    <button
                      className="cr-btn cr-btn-secondary cr-btn-sm"
                      onClick={() =>
                        setCvData((d) => ({
                          ...d,
                          certifications: [
                            ...(d.certifications || []),
                            { titre: '', etablissement: '', annee: '' },
                          ],
                        }))
                      }
                    >
                      <Plus size={13} />
                      Ajouter
                    </button>
                  </div>

                  <div className="cr-stack-sm">
                    {(cvData?.certifications || []).map((c, i) => (
                      <div key={i} className="cr-inline-row cr-inline-row--4">
                        <div className="cr-field">
                          <label className="cr-label">Titre</label>
                          <input
                            className="cr-input"
                            value={c.titre || ''}
                            onChange={(e) => {
                              const certs = [...cvData.certifications];
                              certs[i] = { ...certs[i], titre: e.target.value };
                              setCvData((d) => ({ ...d, certifications: certs }));
                            }}
                          />
                        </div>

                        <div className="cr-field">
                          <label className="cr-label">Organisme</label>
                          <input
                            className="cr-input"
                            value={c.etablissement || ''}
                            onChange={(e) => {
                              const certs = [...cvData.certifications];
                              certs[i] = { ...certs[i], etablissement: e.target.value };
                              setCvData((d) => ({ ...d, certifications: certs }));
                            }}
                          />
                        </div>

                        <div className="cr-field">
                          <label className="cr-label">Année</label>
                          <input
                            className="cr-input"
                            value={c.annee || ''}
                            onChange={(e) => {
                              const certs = [...cvData.certifications];
                              certs[i] = { ...certs[i], annee: e.target.value };
                              setCvData((d) => ({ ...d, certifications: certs }));
                            }}
                          />
                        </div>

                        <button
                          className="cr-btn cr-btn-danger cr-btn-icon cr-btn-sm cr-inline-action"
                          onClick={() =>
                            setCvData((d) => ({
                              ...d,
                              certifications: d.certifications.filter((_, idx) => idx !== i),
                            }))
                          }
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="cr-card">
              <button
                type="button"
                className="cr-card-header cr-card-header--button"
                onClick={() => toggle('formations')}
              >
                <div className="cr-card-title-wrap">
                  <GraduationCap size={16} color="var(--cr-blue-600)" />
                  <span className="cr-card-title">Formations</span>
                </div>
                {expanded.formations ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {expanded.formations && (
                <div className="cr-card-body">
                  <div className="cr-card-toolbar">
                    <button
                      className="cr-btn cr-btn-secondary cr-btn-sm"
                      onClick={() =>
                        setCvData((d) => ({
                          ...d,
                          formations: [...(d.formations || []), { diplome: '', ecole: '', annee: '' }],
                        }))
                      }
                    >
                      <Plus size={13} />
                      Ajouter
                    </button>
                  </div>

                  <div className="cr-stack-sm">
                    {(cvData?.formations || []).map((f, i) => (
                      <div key={i} className="cr-inline-row cr-inline-row--4">
                        <div className="cr-field">
                          <label className="cr-label">Diplôme</label>
                          <input
                            className="cr-input"
                            value={f.diplome || ''}
                            onChange={(e) => {
                              const fms = [...cvData.formations];
                              fms[i] = { ...fms[i], diplome: e.target.value };
                              setCvData((d) => ({ ...d, formations: fms }));
                            }}
                          />
                        </div>

                        <div className="cr-field">
                          <label className="cr-label">École</label>
                          <input
                            className="cr-input"
                            value={f.ecole || ''}
                            onChange={(e) => {
                              const fms = [...cvData.formations];
                              fms[i] = { ...fms[i], ecole: e.target.value };
                              setCvData((d) => ({ ...d, formations: fms }));
                            }}
                          />
                        </div>

                        <div className="cr-field">
                          <label className="cr-label">Année</label>
                          <input
                            className="cr-input"
                            value={f.annee || ''}
                            onChange={(e) => {
                              const fms = [...cvData.formations];
                              fms[i] = { ...fms[i], annee: e.target.value };
                              setCvData((d) => ({ ...d, formations: fms }));
                            }}
                          />
                        </div>

                        <button
                          className="cr-btn cr-btn-danger cr-btn-icon cr-btn-sm cr-inline-action"
                          onClick={() =>
                            setCvData((d) => ({
                              ...d,
                              formations: d.formations.filter((_, idx) => idx !== i),
                            }))
                          }
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <div className="cr-save-wrap">
              <div className="cr-save-card">
                <p className="cr-save-text">
                  Vérifiez soigneusement vos informations avant validation finale.
                </p>

                <button className="cr-btn cr-btn-primary cr-btn-lg" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <div className="cr-spinner cr-spinner--light" />
                      Enregistrement…
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Enregistrer et envoyer
                    </>
                  )}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}